import { FC, memo, useEffect, useImperativeHandle, useRef } from 'react'
import { StaticImageData } from 'next/image'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import css from './PageProject.module.scss'

import routes from '@/data/routes'
import { PageCopyType, PageHandle, PageProps } from '@/data/types'

import { toRem, updateCustomProperties } from '@/utils/basic-functions'
import { MOTION } from '@/utils/motion-values'
import sass from '@/utils/sass'

import useLayout from '@/hooks/use-layout'
import { useRefs } from '@/hooks/use-refs'
import useScrollToTop from '@/hooks/use-scroll-to-top'

import BaseImage from '@/components/BaseImage/BaseImage'
import BaseVideo from '@/components/BaseVideo/BaseVideo'
import Caption from '@/components/Caption/Caption'
import CtaLink from '@/components/CtaLink/CtaLink'
import Heading from '@/components/Heading/Heading'
import Html from '@/components/Html/Html'
import Label from '@/components/Label/Label'
import PageNavigation from '@/components/PageNavigation/PageNavigation'
import TeamModal from '@/components/TeamModal/TeamModal'

export interface PageProjectProps extends PageProps {
  className?: string
  content: PageCopyType
}

export type ViewRefs = {
  root: HTMLDivElement
  mainTitle: HTMLHeadingElement
  link: HTMLAnchorElement
  textContainer: HTMLDivElement
  animateOutPanel1: HTMLDivElement
  animateOutPanel2: HTMLDivElement
  topContainer: HTMLDivElement
  midContainer: HTMLDivElement
}

const View: FC<PageProjectProps> = ({ className, content, onReady }) => {
  const layout = useLayout()
  const viewRefs = useRefs<ViewRefs>()
  const handleRef = useRef<PageHandle>(null)
  const router = useRouter()

  useEffect(() => {
    gsap.set([viewRefs.topContainer.current, viewRefs.midContainer.current], { opacity: 0 })

    setTimeout(() => {
      onReady?.(handleRef)
    }, MOTION.general.delayForLayout)
  }, [onReady, viewRefs])

  useImperativeHandle(handleRef, () => ({
    animateIn: () => {
      const firstGalleryItem = document.querySelector(`.${css.galleryItem}`)

      const timeline = gsap
        .timeline()
        .set([viewRefs.topContainer.current, viewRefs.midContainer.current], { opacity: 1 }, 0)
        .textRiseByLinesIn(
          viewRefs.mainTitle.current!,
          {
            duration: 1.5,
            lineDuration: 5,
            lineOffset: 0.5,
            ease: 'ease-split-text',
            immediateRender: true
          },
          '<+0.1'
        )

      if (viewRefs.link.current) {
        timeline.from(
          viewRefs.link.current,
          { opacity: 0, immediateRender: true, duration: 0.8, ease: 'ease-opacity' },
          '<+0.6'
        )
      }

      if (firstGalleryItem) {
        timeline.from(
          firstGalleryItem,
          { y: 100, duration: 1.2, ease: 'ease-transform', immediateRender: true },
          '<+0.2'
        )
        timeline.from(firstGalleryItem, { opacity: 0, duration: 0.8, ease: 'ease-opacity', immediateRender: true }, '<')
      }

      timeline.from(viewRefs.textContainer.current, { y: 100, duration: 1.2, ease: 'ease-transform' }, '<+0.2')
      timeline.from(viewRefs.textContainer.current, { opacity: 0, duration: 0.8, ease: 'ease-opacity' }, '<')

      return timeline
    },
    animateOut: () => {
      const duration = MOTION.general.wipeLayers.duration

      return gsap
        .timeline()
        .to(viewRefs.animateOutPanel1.current, { yPercent: 0, duration, ease: 'ease-x-y-percent' }, 0)
        .to(viewRefs.animateOutPanel1.current, { yPercent: -100, duration, ease: 'ease-x-y-percent' }, duration)
        .to(viewRefs.animateOutPanel2.current, { yPercent: 0, duration, ease: 'ease-x-y-percent' }, duration)
        .to(viewRefs.animateOutPanel2.current, { yPercent: -100, duration, ease: 'ease-x-y-percent' }, duration * 2)
        .set([viewRefs.topContainer.current, viewRefs.midContainer.current], { opacity: 0 }, '>')
    }
  }))

  useEffect(() => {
    const media = Array.from(document.querySelectorAll(`.${css.galleryItem}`))
    const mediaToAnimate = media.slice(1)

    gsap.set(mediaToAnimate, { opacity: 0, y: toRem(90) })

    const triggers = ScrollTrigger.batch(mediaToAnimate, {
      start: 'top 70%',
      batchMax: 3, // maximum batch size (targets)
      interval: 0.1, // time window (when to fire)
      onEnter: (batch) => {
        gsap
          .timeline()
          .to(batch, { duration: 0.8, opacity: 1, ease: 'ease-opacity', stagger: 0.15, overwrite: true }, 0)
          .to(batch, { duration: 1.2, y: 0, ease: 'ease-image', stagger: 0.15 }, 0)
      }
    })

    return () => {
      triggers.forEach((trigger) => trigger.kill())
    }
  }, [])

  // @NOTE: This is to avoid an issue
  // w/ scrolling logic and Team Modal
  useScrollToTop()

  useEffect(() => {
    const onBeforeHistoryChange = (e: string) => {
      const colorChangingPanel = viewRefs.animateOutPanel2.current

      switch (e) {
        case routes.Home.path:
        case routes.Photo.path:
        case routes.Gallery.path:
        case routes.Work.path:
        case routes.NotFound.path:
          gsap.set(colorChangingPanel, {
            backgroundColor: sass.white
          })
          break
        default: {
          if (e.includes(routes.Work.path)) {
            gsap.set(colorChangingPanel, {
              backgroundColor: sass.black
            })
          }
          break
        }
      }
    }

    router.events.on('beforeHistoryChange', onBeforeHistoryChange)
    return () => {
      router.events.off('beforeHistoryChange', onBeforeHistoryChange)
    }
  }, [router, viewRefs])

  useEffect(() => {
    updateCustomProperties({
      backButtonVisibility: 'visible',
      backButtonColor: sass.white,
      backButtonOpacity: '1',
      navButtonBackground: sass.white,
      navButtonColor: sass.black,
      navButtonBorder: 'transparent'
    })
  }, [])

  return (
    <div className={classNames('PageProject', css.root, className)} ref={viewRefs.root}>
      <div className={css.topContainer} ref={viewRefs.topContainer}>
        <Heading as="h4" italic className={css.title} ref={viewRefs.mainTitle}>
          {content.main.description}
        </Heading>
        {content.main.link ? (
          <CtaLink href={content.main.link.url} showExternalSvg ref={viewRefs.link} withLineHover>
            <Label labelType={3}>{content.main.link.label}</Label>
          </CtaLink>
        ) : null}
      </div>

      <div className={css.midContainer} ref={viewRefs.midContainer}>
        <div className={css.mediaContainer}>
          {content.media.map((mediaData, i) => {
            const media = layout.mobile ? mediaData.mobile : mediaData.desktop
            const isVideo = /\.(mp4|webm|ogg)$/i.test(media.src)
            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(media.src)

            if (isVideo) {
              return (
                <div className={css.galleryItem} key={i}>
                  <BaseVideo className={css.galleryVideo} url={media.src} />
                </div>
              )
            } else if (isImage) {
              return (
                <div className={css.galleryItem} key={i}>
                  <BaseImage
                    className={css.galleryImage}
                    data={media as StaticImageData}
                    fill={false}
                    priority={i === 0 ? true : false}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              )
            }
          })}
        </div>

        <div className={css.textContainer} ref={viewRefs.textContainer}>
          {content.about.description ? (
            <div className={css.about}>
              <Label labelType={layout.desktop ? 3 : 2} className={css.label}>
                About
              </Label>
              <Caption uppercase={false}>
                <Html html={content.about.description} />
              </Caption>
            </div>
          ) : null}
          {content.about.details && content.about.details.length > 0 ? (
            <div className={css.details}>
              <Label labelType={layout.desktop ? 3 : 2} className={css.label}>
                Details
              </Label>
              <ul className={css.list}>
                {content.about?.details?.map((detail, index) => (
                  <Caption as="li" uppercase={false} key={index}>
                    {detail}
                  </Caption>
                ))}
              </ul>
              {content.about.team && content.about.team.length > 0 ? (
                <TeamModal
                  className={css.credits}
                  content={content.about.team}
                  columns={content.styles.teamColumnCount}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <PageNavigation prev={content.navigation.prev} next={content.navigation.next} />

      <div className={css.animateOutContainer}>
        <div className={css.panel1} ref={viewRefs.animateOutPanel1} />
        <div className={css.panel2} ref={viewRefs.animateOutPanel2} />
      </div>
    </div>
  )
}

View.displayName = 'PageProject-View'

// Controller (handles global state, router, data fetching, etc. Feeds props to the view component)
const PageProject: FC<PageProjectProps> = (props) => {
  return <View {...props} />
}

PageProject.displayName = 'PageProject'

export default memo(PageProject)
